/**
 * AccessorySelector - UI component for selecting worm accessories
 *
 * Features:
 * - Inner tabs for Hats and Face accessories
 * - Grid of accessory options with real accessory preview drawings
 * - Currently selected accessory highlighted
 * - Saves selection to localStorage
 */

import {
	AccessoryType,
	ACCESSORY_LIST,
	Canvas2DAdapter,
	drawAccessory as drawSharedAccessory,
	type AccessoryInfo,
	type AccessoryCategory,
} from '@wooorms/shared';

// Re-export AccessoryType for backward compatibility
export { AccessoryType };

const STORAGE_KEY = 'wooorms_accessory';

/** Inner tab types */
type AccessoryTab = 'hats' | 'eyes' | 'mouth' | 'neck';

/**
 * Event handlers for AccessorySelector
 */
export interface AccessorySelectorEvents {
	onAccessoryChange: (accessory: AccessoryType) => void;
}

/**
 * AccessorySelector - UI for selecting worm accessories
 */
export class AccessorySelector {
	/** Container element */
	private container: HTMLDivElement;

	/** Currently selected accessory */
	private selectedAccessory: AccessoryType;

	/** Current active tab */
	private activeTab: AccessoryTab = 'hats';

	/** Event handlers */
	private events: AccessorySelectorEvents;

	/** Button elements for updating selection state */
	private buttons: Map<AccessoryType, HTMLButtonElement> = new Map();

	/** Tab buttons */
	private tabButtons: Map<AccessoryTab, HTMLButtonElement> = new Map();

	/** Grid container for accessories */
	private grid: HTMLDivElement | null = null;

	constructor(events: AccessorySelectorEvents) {
		this.events = events;
		this.selectedAccessory = this.loadFromStorage();

		// Create container
		this.container = document.createElement('div');
		this.container.style.cssText = `
      width: 100%;
      margin-top: 8px;
    `;

		this.createUI();
	}

	/**
	 * Load accessory from localStorage
	 */
	private loadFromStorage(): AccessoryType {
		try {
			const stored = localStorage.getItem(STORAGE_KEY);
			if (stored && Object.values(AccessoryType).includes(stored as AccessoryType)) {
				return stored as AccessoryType;
			}
		} catch {
			// Ignore storage errors
		}
		return AccessoryType.NONE;
	}

	/**
	 * Save accessory to localStorage
	 */
	private saveToStorage(): void {
		try {
			localStorage.setItem(STORAGE_KEY, this.selectedAccessory);
		} catch {
			// Ignore storage errors
		}
	}

	/**
	 * Get accessories for a given tab
	 */
	private getAccessoriesForTab(tab: AccessoryTab): AccessoryInfo[] {
		// Define which categories belong to which tab
		// We map the stored 'category' to the UI tab
		const mappings: Record<AccessoryTab, AccessoryCategory[]> = {
			hats: ['hats', 'top'],
			eyes: ['eyes', 'face'], // Include legacy 'face' here
			mouth: ['mouth'],
			neck: ['neck'],
		};

		const targetCategories = mappings[tab] || [];

		// Filter accessories by category, always include NONE at the start
		// Note: NONE is categorized as 'hats' by default in the list, but we want it available everywhere?
		// Actually, distinct NONE might be better, or just have one "Unequip" button per tab.
		// For now, let's just show items that match category, PLUS 'none' if it matches the current tab's logic
		// OR we can make 'None' available in all tabs.
		// Let's rely on the list having a NONE item.

		// We want a "None" option available in EVERY tab so user can clear that slot.
		// Since we only have ONE accessory slot right now, "None" clears current accessory.
		// We'll synthesise a "None" option if not found, or just grab the global one.

		const accessories = ACCESSORY_LIST.filter(
			(a) => targetCategories.includes(a.category)
		);

		// Always preach "None" at the top of the list for easy removal
		const noneItem = ACCESSORY_LIST.find(a => a.type === AccessoryType.NONE);
		if (noneItem) {
			return [noneItem, ...accessories];
		}

		return accessories;
	}

	/**
	 * Create the UI
	 */
	private createUI(): void {
		// Create tab bar
		const tabBar = document.createElement('div');
		tabBar.style.cssText = `
      display: flex;
      gap: 6px;
      margin-bottom: 12px;
      background: rgba(0, 0, 0, 0.2);
      padding: 4px;
      border-radius: 8px;
    `;

		const tabs: { id: AccessoryTab; label: string; icon: string }[] = [
			{ id: 'hats', label: 'Hats', icon: '🎩' },
			{ id: 'eyes', label: 'Eyes', icon: '👓' },
			{ id: 'mouth', label: 'Mouth', icon: '👄' },
			{ id: 'neck', label: 'Neck', icon: '🧣' },
		];

		tabs.forEach(t => {
			const btn = this.createTabButton(t.id, `${t.icon} ${t.label}`);
			this.tabButtons.set(t.id, btn);
			tabBar.appendChild(btn);
		});

		this.container.appendChild(tabBar);

		// Create grid container
		this.grid = document.createElement('div');
		this.grid.id = 'accessory-grid';
		this.grid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 12px;
      padding: 0;
      /* background: handled by parent */
      /* border: handled by parent */
    `;

		// Add responsive grid styles for larger screens
		const gridStyles = document.createElement('style');
		gridStyles.id = 'accessory-grid-styles';
		if (!document.getElementById('accessory-grid-styles')) {
			gridStyles.textContent = `
        /* Standard: 5 columns */
        #accessory-grid {
          grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
        }
        /* Scrollbar styling */
        #accessory-grid::-webkit-scrollbar {
          width: 8px;
        }
        #accessory-grid::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 4px;
        }
        #accessory-grid::-webkit-scrollbar-thumb {
          background: rgba(78, 205, 196, 0.5);
          border-radius: 4px;
        }
        #accessory-grid::-webkit-scrollbar-thumb:hover {
          background: rgba(78, 205, 196, 0.8);
        }
        /* Large desktop (1440px+): 6 columns */
        @media (min-width: 1440px) {
          #accessory-grid {
            grid-template-columns: repeat(6, 1fr) !important;
            gap: 10px !important;
          }
        }
        /* Extra large (1800px+): 7 columns */
        @media (min-width: 1800px) {
          #accessory-grid {
            grid-template-columns: repeat(7, 1fr) !important;
            gap: 12px !important;
          }
        }
        /* Mobile: 4 columns */
        @media (max-width: 600px) {
          #accessory-grid {
            grid-template-columns: repeat(4, 1fr) !important;
            gap: 6px !important;
            padding: 8px !important;
          }
        }
      `;
			document.head.appendChild(gridStyles);
		}

		this.container.appendChild(this.grid);

		// Populate grid with initial tab
		this.switchTab(this.activeTab);
	}

	/**
	 * Create a tab button
	 */
	private createTabButton(tab: AccessoryTab, label: string): HTMLButtonElement {
		const isActive = tab === this.activeTab;

		const button = document.createElement('button');
		button.textContent = label;
		button.style.cssText = `
      flex: 1;
      padding: 8px 12px;
      border-radius: 6px;
      border: 2px solid ${isActive ? '#4ecdc4' : 'rgba(78, 205, 196, 0.3)'};
      background: ${isActive ? 'rgba(78, 205, 196, 0.2)' : 'rgba(40, 40, 55, 0.6)'};
      color: ${isActive ? '#4ecdc4' : '#888'};
      font-size: 12px;
      font-weight: bold;
      cursor: pointer;
      transition: all 0.15s ease;
    `;

		button.addEventListener('mouseover', () => {
			if (tab !== this.activeTab) {
				button.style.background = 'rgba(78, 205, 196, 0.1)';
				button.style.color = '#aaa';
			}
		});

		button.addEventListener('mouseout', () => {
			if (tab !== this.activeTab) {
				button.style.background = 'rgba(40, 40, 55, 0.6)';
				button.style.color = '#888';
			}
		});

		button.addEventListener('click', () => {
			this.switchTab(tab);
		});

		return button;
	}

	/**
	 * Switch to a different tab
	 */
	private switchTab(tab: AccessoryTab): void {
		if (!this.grid) return;

		this.activeTab = tab;

		// Update tab button styles
		this.tabButtons.forEach((button, buttonTab) => {
			const isActive = buttonTab === tab;
			button.style.border = `2px solid ${isActive ? '#4ecdc4' : 'rgba(78, 205, 196, 0.3)'}`;
			button.style.background = isActive ? 'rgba(78, 205, 196, 0.2)' : 'rgba(40, 40, 55, 0.6)';
			button.style.color = isActive ? '#4ecdc4' : '#888';
		});

		// Clear grid and buttons
		this.grid.innerHTML = '';
		this.buttons.clear();

		// Populate with accessories for this tab
		const accessories = this.getAccessoriesForTab(tab);
		accessories.forEach((accessory) => {
			const button = this.createAccessoryButton(accessory);
			this.buttons.set(accessory.type, button);
			this.grid!.appendChild(button);
		});
	}

	/**
	 * Draw accessory preview on a canvas
	 */
	private drawAccessoryPreview(canvas: HTMLCanvasElement, type: AccessoryType): void {
		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		const width = canvas.width;
		const height = canvas.height;
		ctx.clearRect(0, 0, width, height);

		if (type === AccessoryType.NONE) {
			// Draw X for "none" - centered in canvas
			const centerX = width / 2;
			const centerY = height / 2;
			const xSize = Math.min(width, height) * 0.25;
			ctx.strokeStyle = '#ff4444';
			ctx.lineWidth = 4;
			ctx.lineCap = 'round';
			ctx.beginPath();
			ctx.moveTo(centerX - xSize, centerY - xSize);
			ctx.lineTo(centerX + xSize, centerY + xSize);
			ctx.moveTo(centerX + xSize, centerY - xSize);
			ctx.lineTo(centerX - xSize, centerY + xSize);
			ctx.stroke();
			return;
		}

		// Draw the actual accessory on canvas
		// Position at 65% from top - gives ample room for tall hats above
		ctx.save();
		ctx.translate(width / 2, height * 0.65);
		// Radius sized to fit accessories nicely
		const radius = width * 0.30;
		const adapter = new Canvas2DAdapter(ctx);
		drawSharedAccessory(adapter, type, radius);
		ctx.restore();
	}

	/**
	 * Create a button for an accessory
	 */
	private createAccessoryButton(accessory: AccessoryInfo): HTMLButtonElement {
		const isSelected = accessory.type === this.selectedAccessory;

		const button = document.createElement('button');
		button.title = accessory.name;
		button.style.cssText = `
      width: 100%;
      aspect-ratio: 0.85;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      position: relative;
      border-radius: 12px;
      border: 2px solid ${isSelected ? '#4ecdc4' : 'rgba(255, 255, 255, 0.05)'};
      background: ${isSelected ? 'rgba(78, 205, 196, 0.15)' : 'rgba(255, 255, 255, 0.03)'};
      cursor: pointer;
      transition: all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
      padding: 8px 4px 20px 4px;
      overflow: visible;
      box-shadow: ${isSelected ? '0 0 15px rgba(78, 205, 196, 0.3)' : 'none'};
    `;

		// Create canvas for accessory preview (taller for hats)
		const canvas = document.createElement('canvas');
		const canvasWidth = 96;
		const canvasHeight = 120; // Taller to fit tall hats
		canvas.width = canvasWidth;
		canvas.height = canvasHeight;
		canvas.style.cssText = `
      width: 60px;
      height: 75px;
      flex-shrink: 0;
    `;
		this.drawAccessoryPreview(canvas, accessory.type);

		// Name positioned at the bottom of the button
		const nameSpan = document.createElement('span');
		nameSpan.textContent = accessory.name;
		nameSpan.style.cssText = `
      position: absolute;
      bottom: 4px;
      left: 2px;
      right: 2px;
      font-size: 9px;
      color: ${isSelected ? '#4ecdc4' : '#999'};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-align: center;
      transition: color 0.15s;
    `;

		button.appendChild(canvas);
		button.appendChild(nameSpan);

		// Hover effects
		// Hover effects
		button.addEventListener('mouseover', () => {
			if (accessory.type !== this.selectedAccessory) {
				button.style.background = 'rgba(255, 255, 255, 0.08)';
				button.style.transform = 'translateY(-4px) scale(1.02)';
				button.style.boxShadow = '0 5px 15px rgba(0,0,0,0.3)';
				button.style.border = '2px solid rgba(255, 255, 255, 0.2)';
				nameSpan.style.color = '#fff';
			}
		});

		button.addEventListener('mouseout', () => {
			if (accessory.type !== this.selectedAccessory) {
				button.style.background = 'rgba(255, 255, 255, 0.03)';
				button.style.transform = 'translateY(0) scale(1)';
				button.style.boxShadow = 'none';
				button.style.border = '2px solid rgba(255, 255, 255, 0.05)';
				nameSpan.style.color = '#999';
			}
		});

		// Click handler
		button.addEventListener('click', () => {
			this.selectAccessory(accessory.type);
		});

		return button;
	}

	/**
	 * Select an accessory
	 */
	private selectAccessory(type: AccessoryType): void {
		if (type === this.selectedAccessory) return;

		// Update previous button (if visible in current tab)
		const prevButton = this.buttons.get(this.selectedAccessory);
		if (prevButton) {
			prevButton.style.border = '2px solid rgba(255, 255, 255, 0.05)';
			prevButton.style.background = 'rgba(255, 255, 255, 0.03)';
			prevButton.style.boxShadow = 'none';
			prevButton.style.transform = 'none';
			const nameSpan = prevButton.querySelector('span:last-child') as HTMLSpanElement;
			if (nameSpan) nameSpan.style.color = '#999';
		}

		// Update new button
		this.selectedAccessory = type;
		const newButton = this.buttons.get(type);
		if (newButton) {
			newButton.style.border = '2px solid #4ecdc4';
			newButton.style.background = 'rgba(78, 205, 196, 0.15)';
			newButton.style.boxShadow = '0 0 15px rgba(78, 205, 196, 0.3)';
			newButton.style.transform = 'translateY(-2px)';
			const nameSpan = newButton.querySelector('span:last-child') as HTMLSpanElement;
			if (nameSpan) nameSpan.style.color = '#4ecdc4';
		}

		// Save and notify
		this.saveToStorage();
		this.events.onAccessoryChange(type);
	}

	/**
	 * Get the selected accessory
	 */
	getSelectedAccessory(): AccessoryType {
		return this.selectedAccessory;
	}

	/**
	 * Get the container element
	 */
	getElement(): HTMLDivElement {
		return this.container;
	}

	/**
	 * Destroy the component
	 */
	destroy(): void {
		this.buttons.clear();
		this.tabButtons.clear();
		this.container.remove();
	}
}
